/**
 * Scroll Depth Drill Panel
 *
 * Slide-in panel showing detailed scroll depth data for a single page.
 * Follows the same pattern as ChannelDrillPanel and PageDrillPanel.
 */

import React, { useCallback, useEffect } from 'react';
import { X, ArrowDownRight } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { slideInRight, fadeBackdrop } from '@/lib/motionVariants';
import { formatNumber } from '@/lib/formatting';
import { BRAND } from '../charts/utils';
import { useChatStore } from '@/stores/chatStore';
import type { GA4ScrollDepthEntry } from '@/types/dashboard';

interface ScrollDepthPanelProps {
  entry: GA4ScrollDepthEntry | null;
  open: boolean;
  onClose: () => void;
}

const THRESHOLD_LABELS: Record<string, string> = {
  '25': 'Above the fold',
  '50': 'Mid-page',
  '75': 'Deep engagement',
  '90': 'Near bottom (GA4 default)',
  '100': 'Full page read',
};

const THRESHOLD_COLORS: Record<string, string> = {
  '25': '#10b981',
  '50': '#3b82f6',
  '75': '#f59e0b',
  '90': '#A4863D',
  '100': '#A4863D',
};

export default function ScrollDepthPanel({ entry, open, onClose }: ScrollDepthPanelProps) {
  const chatWidth = useChatStore(s => s.isOpen ? (s.panelWidth === 'wide' ? 600 : s.panelWidth === 'medium' ? 480 : 380) : 0);

  useEffect(() => {
    if (!open) return;
    const handler = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [open, onClose]);

  const handleAskAI = useCallback(() => {
    if (!entry) return;
    const query = `Analyze scroll depth for the "${entry.page}" page. What does the drop-off between thresholds tell us about content engagement? How can we improve it?`;
    useChatStore.getState().sendAnalyzeQuery(query);
  }, [entry]);

  if (!entry) return null;

  const thresholds = Object.entries(entry.depths).sort(([a], [b]) => parseInt(a) - parseInt(b));
  const maxCount = thresholds.length > 0 ? Math.max(...thresholds.map(([, v]) => v)) : 1;

  return (
    <AnimatePresence>
      {open && (
        <>
          <motion.div
            className="fixed inset-0 bg-black/30 z-40"
            variants={fadeBackdrop}
            initial="hidden"
            animate="visible"
            exit="hidden"
            onClick={onClose}
          />
          <motion.div
            className="fixed top-0 bottom-0 z-50 w-[420px] bg-bg-0 border-l border-border shadow-elevated overflow-y-auto"
            style={{ right: chatWidth }}
            variants={slideInRight}
            initial="hidden"
            animate="visible"
            exit="hidden"
          >
            <div className="sticky top-0 bg-bg-0 border-b border-border px-6 py-4 flex items-center justify-between z-10">
              <div className="flex items-center gap-2">
                <ArrowDownRight className="w-4 h-4 text-monarch-gold" />
                <h2 className="font-display text-base font-semibold text-text truncate max-w-[280px]">{entry.page}</h2>
              </div>
              <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-bg-2 transition-colors">
                <X className="w-4 h-4 text-text-3" />
              </button>
            </div>

            <div className="p-6 space-y-6">
              <div>
                <h3 className="text-xs text-text-3 uppercase tracking-wider font-semibold mb-3">Scroll Depth Thresholds</h3>
                <div className="space-y-3">
                  {thresholds.map(([threshold, count]) => (
                    <div key={threshold}>
                      <div className="flex items-center justify-between mb-1">
                        <div>
                          <span className="text-sm font-medium text-text">{threshold}%</span>
                          <span className="text-xs text-text-3 ml-2">{THRESHOLD_LABELS[threshold] || ''}</span>
                        </div>
                        <span className="font-mono text-sm text-text">{formatNumber(count)}</span>
                      </div>
                      <div className="w-full h-3 bg-bg-2 rounded-full overflow-hidden">
                        <div
                          className="h-full rounded-full transition-all duration-500"
                          style={{
                            width: `${(count / maxCount) * 100}%`,
                            backgroundColor: THRESHOLD_COLORS[threshold] || BRAND.gold,
                          }}
                        />
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {thresholds.length >= 2 && (
                <div>
                  <h3 className="text-xs text-text-3 uppercase tracking-wider font-semibold mb-3">Drop-Off Between Thresholds</h3>
                  <div className="space-y-2">
                    {thresholds.slice(1).map(([threshold, count], idx) => {
                      const prevCount = thresholds[idx][1];
                      const dropOff = prevCount > 0 ? Math.round(((prevCount - count) / prevCount) * 100) : 0;
                      return (
                        <div key={threshold} className="flex items-center justify-between text-sm">
                          <span className="text-text-3">{thresholds[idx][0]}% &rarr; {threshold}%</span>
                          <span className={`font-mono font-semibold ${dropOff > 50 ? 'text-danger' : dropOff > 25 ? 'text-warning' : 'text-success'}`}>
                            -{dropOff}% drop-off
                          </span>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              <button
                onClick={handleAskAI}
                className="w-full py-2.5 rounded-lg bg-monarch-gold/10 border border-monarch-gold/30 text-monarch-gold text-sm font-semibold hover:bg-monarch-gold/20 transition-colors"
              >
                Ask AI about this page's scroll behavior
              </button>
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
}
